import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import {
  Icon,
  theme,
  Text,
  Select,
  SelectOption,
  message,
  Spin,
  Tag,
} from 'components';
import { startCase } from 'lodash';
import errorReporter from '@app/errorReporter';
import { useDebounce } from 'hooks';
import { PopulatedUser } from 'api/UserController/getProfile';
import { getSupraLockboxData } from 'api/SupraLockboxController';
import { featureEnabled } from 'utils';
import type { SupraLockboxJsonType } from '@/app-package';
import AddLockboxModalDisplay from './AddLockboxModalDisplay';
import AddLockboxModalWithShackleDisplay from './AddLockboxModalWithShackle';
import { LockboxPermissionType, ShackleSetting } from './types';

type LockboxSearchPropsType = {
  mlsStaffOrgId?: string;
  user?: PopulatedUser;
  unassignedLockboxes: SupraLockboxJsonType[];
  onValueChange: (lockboxes: any) => void;
  setOptions: React.Dispatch<React.SetStateAction<SupraLockboxJsonType[]>>;
  options: SupraLockboxJsonType[];
  values: string[];
  setValues: React.Dispatch<React.SetStateAction<string[]>>;
  permission: LockboxPermissionType | undefined;
  shackleSetting: ShackleSetting;
  shackleUserIds: string[] | undefined;
  onPermissionChange: (
    lockboxId: string,
    permissionType: LockboxPermissionType,
  ) => void;
  onShackleSettingChange: (setting: ShackleSetting) => void;
  onShackleUserIdsChange: (userIds: string[] | undefined) => void;
};

const LockboxSearch = ({
  mlsStaffOrgId,
  user,
  unassignedLockboxes,
  onValueChange,
  options,
  setOptions,
  values,
  setValues,
  permission,
  shackleSetting,
  shackleUserIds,
  onPermissionChange,
  onShackleSettingChange,
  onShackleUserIdsChange,
}: LockboxSearchPropsType) => {
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(false);

  const debounceQuery = useDebounce(query, 500);

  useEffect(() => {
    if (!debounceQuery) return;
    const getLockboxList = async () => {
      setLoading(true);
      try {
        const { lockboxes } = await getSupraLockboxData({
          mlsStaffOrgId,
          ownerId: user?.org?.id,
          ownerType: 'org',
          assigneeId: user?.org?.id,
          assigneeType: 'org',
          searchText: debounceQuery,
          lockboxStatuses: ['New', 'Used', 'Active'],
          requireOwnerAsMLS: true,
          filterBySupraOwnerMapping: false,
        });
        setOptions(lockboxes);
      } catch (err) {
        message.error(err);
        errorReporter.error(err);
      }

      setLoading(false);
    };

    getLockboxList();
  }, [debounceQuery, mlsStaffOrgId, setOptions, user?.org.id]);

  const getOwnerOrAssigneeLabel = (
    type: string,
    mlsOwnerName?: string,
    branchOwnerName?: string,
    orgOwnerName?: string,
  ) => {
    switch (type) {
      case 'branch':
        return branchOwnerName || 'Branch';
      case 'org':
        return orgOwnerName || 'Brokerage';
      case 'mls':
        return mlsOwnerName || 'MLS';
      default:
        return startCase(type);
    }
  };

  return (
    <StyledSearchContainer>
      <StyledFlexContainer>
        <StyledSearchIcon type="search" width="16px" />
        <StyledSelect
          label="Search lockboxes"
          optionLabelProp="label"
          mode="multiple"
          placeholder="Search Serial #"
          value={values}
          tagRender={prop => (
            <StyledTag
              color="gray"
              closable
              onClose={e => {
                const newValues = values.filter(v => v !== prop.value);
                onValueChange(newValues.sort());
                setValues(newValues);
                setQuery('');
              }}
            >
              {prop.label}
            </StyledTag>
          )}
          notFoundContent={
            loading ? (
              <Spin size="small" />
            ) : (
              <StyledNoResultContainer>
                {query ? (
                  <Text type="secondary">No lockboxes found</Text>
                ) : (
                  <Text type="secondary">
                    Search Lockbox with serial number
                  </Text>
                )}
              </StyledNoResultContainer>
            )
          }
          onChange={(newValues: string[]) => {
            onValueChange(newValues.sort());
            setValues([...newValues]);
            setQuery('');
          }}
          searchValue={query}
          onSearch={searchQuery => setQuery(searchQuery)}
        >
          {options.map((lockbox: any) => (
            <SelectOption
              key={lockbox.serialNumber}
              value={lockbox.serialNumber}
              label={lockbox.serialNumber}
            >
              <div>
                {' '}
                {lockbox.serialNumber}
                <Text style={{ marginLeft: '5px' }}>
                  {lockbox.firstName
                    ? `${lockbox.firstName} ${lockbox.lastName}`
                    : getOwnerOrAssigneeLabel(
                        lockbox.ownerType,
                        lockbox.mlsOwnerName,
                        lockbox.branchOwnerName,
                        lockbox.orgOwnerName,
                      )}
                </Text>
              </div>
            </SelectOption>
          ))}
        </StyledSelect>
      </StyledFlexContainer>
      <AddLockboxModalDisplay
        visible={
          !featureEnabled('combinedLockboxAssignShacklePermissionsFeature')
        }
        unassignedLockboxes={unassignedLockboxes}
        onPermissionChange={onPermissionChange}
      />
      <AddLockboxModalWithShackleDisplay
        visible={featureEnabled(
          'combinedLockboxAssignShacklePermissionsFeature',
        )}
        user={user}
        permission={permission}
        shackleSetting={shackleSetting}
        shackleUserIds={shackleUserIds}
        onPermissionChange={onPermissionChange}
        onShackleSettingChange={onShackleSettingChange}
        onShackleUserIdsChange={onShackleUserIdsChange}
      />
    </StyledSearchContainer>
  );
};

export default LockboxSearch;

const StyledTag = styled(Tag)`
  && {
    color: ${theme.gray8};
    border: 1px solid ${theme.gray6};
    border-radius: 0px;
  }
`;

const StyledSelect = styled(Select)`
  width: 100%;
  position: relative;
  border-radius: 2px;
  .ant-select-selection-placeholder {
    padding: 0 0 0 25px;
  }
  .ant-select-selection-overflow {
    margin: 0 0 0 25px;
  }
`;

const StyledSearchIcon = styled(Icon)`
  position: absolute;
  left: 10px;
  top: 30px;
  z-index: 1;
  color: ${theme.gray8};
`;

const StyledFlexContainer = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  width: 100%;
`;

const StyledSearchContainer = styled.div`
  margin-top: 10px;
  width: 100%;
`;

const StyledNoResultContainer = styled.div`
  text-align: center;
  margin: 20px 0px;
`;
